import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import swal from 'sweetalert';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Button from '../../components/bootstrap/Button';
import { getLink, links } from '../../helpers/urls';
import UseToken from '../../helpers/authService';
import { authMenu } from '../../menu';
import Spinner from '../../components/bootstrap/Spinner';

// eslint-disable-next-line react/prop-types
const LoginHeader = () => {
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>Welcome,</div>
			<div className='text-center h4 text-muted mb-5'>Sign in to continue!</div>
		</>
	);
};

async function loginUser(credentials) {
	return fetch(getLink(links.pages.login), {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(credentials),
	}).then((data) => data.json());
}

const SignInSchema = Yup.object().shape({
	email: Yup.string().email('Please enter a valid email').required('This field is required'),
	password: Yup.string().min(8, 'Password too short!').required('This field is required'),
});

const SignIn = () => {
	const isUserLogged = UseToken().isUserLogged();

	const navigate = useNavigate();

	useEffect(() => {
		if (isUserLogged) {
			window.location.href = '/';
		}
	}, [isUserLogged]);

	const [loading, setLoading] = useState(false);

	const formik = useFormik({
		initialValues: {
			email: '',
			password: '',
		},
		validationSchema: SignInSchema,
		onSubmit: async (values) => {
			setLoading(true);
			const response = await loginUser(values);
			setLoading(false);
			if (response.status) {
				UseToken().setAccessToken(response.data.authorisation.token);
				UseToken().getAccessTokenExpiry(response.data.authorisation.expires_in);
				setLoading(true);
				const response2 = await UseToken().getRequest(getLink(links.pages.profile));
				setLoading(false);
				if (response2.status) {
					UseToken().logUserIn();
					UseToken().setUserData(JSON.stringify(response2.data));
					window.location.href = '/';
				} else {
					UseToken().signOut();
					swal('Oops!', response2.message, 'error');
				}
			} else if (response.message === 'unverified') {
				navigate(authMenu.confirmSignUp.path, {
					state: { email: response.data.email, user_id: response.data.id },
				});
			} else swal('Oops!', response.message, 'error');
		},
	});

	return (
		<>
			<LoginHeader />

			<form className='row g-4' onSubmit={formik.handleSubmit} method='POST'>
				<div className='col-12'>
					<FormGroup
						id='login-username'
						className={classNames({ 'mb-3': !formik.errors.email })}
						isFloating
						label='Your email address'>
						<Input
							autoComplete='email'
							type='email'
							name='email'
							disabled={loading}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.email}
							isTouched={formik.touched.email}
							// eslint-disable-next-line no-unneeded-ternary
							isValid={formik.errors.email !== null ? true : false}
							invalidFeedback={formik.errors.email}
						/>
					</FormGroup>
					{formik.errors.email && formik.touched.email && (
						<div className='text-danger text-sm mb-3'>{formik.errors.email}</div>
					)}
					<FormGroup id='login-password' isFloating label='Password'>
						<Input
							type='password'
							autoComplete='password'
							name='password'
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.password}
							isTouched={formik.touched.password}
							disabled={loading}
							// eslint-disable-next-line no-unneeded-ternary
							isValid={formik.errors.password !== null ? true : false}
							invalidFeedback={formik.errors.password}
						/>
					</FormGroup>
					{formik.errors.password && formik.touched.password && (
						<div className='text-danger text-sm mb-3'>{formik.errors.password}</div>
					)}
				</div>
				<div className='col-12 text-center'>
					{loading ? (
						<Button type='submit' color='light' disabled={loading} className='py-3'>
							<Spinner color='primary'>Loading...</Spinner>
						</Button>
					) : (
						<Button
							type='submit'
							color='primary'
							className='w-100 py-3'
							disabled={loading}>
							Login
						</Button>
					)}
				</div>
			</form>
			<div className='mt-2 text-center'>
				<div className='mb-2'>
					<div className='text-decoration-none fw-bold d-inline-block'>
						Forgot your password?
					</div>
					<Link
						to={authMenu.forgetGroup.subMenu.forgetPassword.path}
						className='text-decoration-none text-primary d-inline-block ms-2'>
						Get help
					</Link>
				</div>
			</div>
		</>
	);
};

export default SignIn;
